/**
 * Renderer for the `custom_component` page node (ADR-0022 · open registries #2).
 *
 * Hosts author React in a **sandboxed iframe** and bridges it to the page via
 * `postMessage` (see `lib/customComponent.ts` for the protocol + threat model).
 *
 * Renders LIVE in BOTH surfaces:
 *  - **portal** — model from the live runtime (`serializeModel(rt)`); actions
 *    (setVar/refresh/navigate/toast) dispatch into the runtime;
 *  - **builder** (`editing`) — model from the SAMPLE scope (`modelFromScope`),
 *    so the author sees the real component on the canvas; the frame is made
 *    **inert** (pointer-events: none) so a click selects the node, and actions
 *    are no-ops.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { CustomComponentMode, JsLibraryOut } from "@/lib/api-apps";
import {
  asFrameMessage,
  buildFrameSrcDoc,
  type FrameLibrary,
  frameLibraries,
  modelFromScope,
  parseProps,
  serializeModel,
  transpile,
  type TrayModelSnapshot,
} from "@/lib/customComponent";
import { type Scope } from "@/lib/pageExpr";
import type { PageNode } from "@/lib/pageTypes";
import { uid } from "@/lib/uid";

import type { EditCtx } from "./PageRenderer";
import type { PageRuntime } from "./PageRuntime";

type ActionDispatch = (action: string, args: unknown[]) => void;

/** A resolved library component: its code + its authoring mode (ADR-0046). */
export interface LibraryComponentEntry {
  code: string;
  mode: CustomComponentMode;
}

/**
 * Library-component resolution for the BUILDER canvas (ADR-0022 #2 · ADR-0046).
 *
 * A `source="library"` node stores only a `component_id`; the PORTAL resolves
 * the live code + mode server-side (injected into `props.code`/`props.mode` at
 * page-load), but the builder renders the editing AST directly, so it needs its
 * own lookup. The builder provides a `componentId → {code, mode}` map via this
 * context; the frame falls back to `props.code`/`props.mode` (portal-injected)
 * when the map has no entry.
 */
export const LibraryCodeContext = createContext<Map<string, LibraryComponentEntry>>(new Map());

/**
 * The app's pinned external JS libraries (ADR-0046), provided once by
 * `PageRenderer` (from the page payload, BOTH planes). The frame loads each from
 * its CDN url and exposes it to author code (`window[global]` + `require`).
 */
export const CustomComponentLibrariesContext = createContext<JsLibraryOut[]>([]);

export function CustomComponentFrame({
  node,
  scope,
  rt,
  editing,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
  editing?: EditCtx;
}) {
  const libraryCode = useContext(LibraryCodeContext);
  const appLibraries = useContext(CustomComponentLibrariesContext);
  const libraries = useMemo<FrameLibrary[]>(() => frameLibraries(appLibraries), [appLibraries]);
  // `library` compiles exactly like `transpile` — the only difference is WHERE
  // the source comes from (the library entry, resolved server-side for the
  // portal or via context for the builder).
  const rawSource = node.props.source;
  const isLibrary = rawSource === "library";
  const source = rawSource === "bundle" ? "bundle" : "transpile";
  const libEntry = isLibrary
    ? libraryCode.get(String(node.props.component_id ?? ""))
    : undefined;
  const code = isLibrary
    ? String(node.props.code ?? libEntry?.code ?? "")
    : String(node.props.code ?? "");
  // The authoring mode (ADR-0046) only varies for a LIBRARY component — inline
  // `transpile`/`bundle` sources are React (JSX) by definition. Portal injects
  // `props.mode`; the builder reads it from the library-code context entry. The
  // resolved mode can change AFTER the first render (the context entry loading a
  // tick late) — the frame re-applies it on the `tray:code` push, so it isn't
  // frozen at the init-time mode.
  const componentMode: CustomComponentMode = isLibrary
    ? node.props.mode === "html" || libEntry?.mode === "html"
      ? "html"
      : "react"
    : "react";
  const bundleUrl = String(node.props.bundle_url ?? "");
  const height = Math.max(60, Math.min(2000, Number(node.props.height ?? 240)));

  const props = useMemo(() => parseProps(node.props.props_json, scope), [node.props.props_json, scope]);

  // Model snapshot: from the live runtime (portal) or the sample scope (builder).
  const model = useMemo<TrayModelSnapshot>(
    () => (rt ? serializeModel(rt) : modelFromScope(scope)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rt, rt?.vars, rt?.queries, rt?.user, rt?.role, scope],
  );

  // Actions only do something in the live portal; inert on the builder canvas.
  const dispatch: ActionDispatch | undefined = rt
    ? (action, a) => {
        if (action === "setVar") rt.setVar(String(a[0] ?? ""), a[1]);
        else if (action === "refresh") rt.refresh();
        else if (action === "navigate") rt.navigate(String(a[0] ?? ""));
        else if (action === "toast") rt.toast(String(a[0] ?? ""));
      }
    : undefined;

  return (
    <LiveFrame
      source={source}
      componentMode={componentMode}
      libraries={libraries}
      code={code}
      bundleUrl={bundleUrl}
      height={height}
      props={props}
      model={model}
      dispatch={dispatch}
      inert={!!editing}
      // Authoring surfaces (builder canvas + library editor: no live runtime)
      // load the DEV React build for full error messages; the live portal keeps
      // the small production build.
      dev={rt === null}
    />
  );
}

function LiveFrame({
  source,
  componentMode,
  libraries,
  code,
  bundleUrl,
  height,
  props,
  model,
  dispatch,
  inert,
  dev,
}: {
  source: "transpile" | "bundle";
  componentMode: CustomComponentMode;
  libraries: FrameLibrary[];
  code: string;
  bundleUrl: string;
  height: number;
  props: Record<string, unknown>;
  model: TrayModelSnapshot;
  dispatch?: ActionDispatch;
  inert: boolean;
  dev: boolean;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // One stable nonce + srcDoc per mounted frame.
  const nonce = useMemo(() => uid("cc"), []);
  const srcDoc = useMemo(() => buildFrameSrcDoc(window.location.origin, dev), [dev]);

  const [compiled, setCompiled] = useState<string | null>(source === "bundle" ? "" : null);
  const [error, setError] = useState<string | null>(null);
  const [helloAt, setHelloAt] = useState(0); // bump → frame is ready for init
  const initedRef = useRef(false);

  // Transpile (parent-side, lazy) whenever the author code changes. HTML mode
  // is plain JS — no JSX transform — so its code passes through verbatim; the
  // bundle source has no parent-side code at all.
  useEffect(() => {
    if (source === "bundle") {
      setCompiled("");
      return;
    }
    if (componentMode === "html") {
      setError(null);
      setCompiled(code);
      return;
    }
    let alive = true;
    setError(null);
    void transpile(code).then((res) => {
      if (!alive) return;
      if ("error" in res) {
        setError(res.error);
        setCompiled(null);
      } else {
        setCompiled(res.code);
      }
    });
    return () => {
      alive = false;
    };
  }, [source, code, componentMode]);

  // Bridge: receive frame messages (only from OUR iframe).
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg = asFrameMessage(e.data);
      if (!msg) return;
      if (msg.type === "tray:hello") setHelloAt((n) => n + 1);
      // A frame RUNTIME error renders inline in the frame's own #root (red
      // pre) — do NOT setError here: that would unmount the iframe, and the
      // remounted one never gets re-inited (so a later valid code change can't
      // recover). Parent-side TRANSPILE errors still use `error` (below).
      else if (msg.type === "tray:action") dispatch?.(msg.action, msg.args);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatch]);

  // Send `tray:init` once the frame said hello AND the payload is ready.
  useEffect(() => {
    if (initedRef.current || !helloAt) return;
    const ready = source === "bundle" ? !!bundleUrl : compiled !== null;
    if (!ready) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: "tray:init",
        nonce,
        mode: source,
        componentMode,
        libraries,
        code: source === "transpile" ? compiled : undefined,
        bundleUrl: source === "bundle" ? bundleUrl : undefined,
        props,
        model,
      },
      "*",
    );
    initedRef.current = true;
  }, [helloAt, compiled, source, componentMode, libraries, bundleUrl, nonce, props, model]);

  // Re-render the frame when the author code changes AFTER the first init
  // (editor live preview, or library code that loaded async): the frame inits
  // exactly once, so a later code change must be pushed explicitly.
  useEffect(() => {
    if (!initedRef.current || source !== "transpile" || compiled === null) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: "tray:code", nonce, componentMode, libraries, code: compiled, props },
      "*",
    );
  }, [compiled, source, componentMode, libraries, nonce, props]);

  // Push live model updates after init (two-way binding).
  useEffect(() => {
    if (!initedRef.current) return;
    iframeRef.current?.contentWindow?.postMessage({ type: "tray:model", nonce, model }, "*");
  }, [model, nonce]);

  if (error) {
    return (
      <div className="rounded-md border border-danger/40 bg-danger/5 p-2 text-xs text-danger">
        <span className="font-medium">Custom component error:</span> {error}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title="Custom component"
      // SECURITY: allow-scripts ONLY — no allow-same-origin → opaque origin, no
      // reach into the parent DOM / cookies / portal token. Never widen this.
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      // On the builder canvas the frame is inert so a click selects the node
      // (and the author's buttons don't fire); live + interactive in the portal.
      style={{
        width: "100%",
        height,
        border: "0",
        display: "block",
        pointerEvents: inert ? "none" : "auto",
      }}
    />
  );
}
